import {
	createContext,
	useContext,
	useCallback,
	useEffect,
	type ReactNode,
} from "react";
import type { Pro } from "./api";
import { useAuth } from "./auth-context";
import { useProMe } from "../hooks/queries/useProQueries";
import { prefetchDashboard } from "./route-loaders";

type ProContextType = {
	pro: Pro | null;
	proId: string | null;
	proRole: string | null;
	onboardingRequired: boolean;
	pendingInvitationToken: string | null;
	isLoading: boolean;
	error: string | null;
	refreshPro: () => Promise<void>;
};

const ProContext = createContext<ProContextType | null>(null);

export function ProProvider({ children }: { children: ReactNode }) {
	const { isAuthenticated, isLoading: authLoading } = useAuth();

	const { data, isLoading, error, refetch } = useProMe(isAuthenticated);

	const refreshPro = useCallback(async () => {
		await refetch();
	}, [refetch]);

	// Warm TanStack Query cache with dashboard data once proId is known
	useEffect(() => {
		if (data?.proId) {
			prefetchDashboard();
		}
	}, [data?.proId]);

	return (
		<ProContext.Provider
			value={{
				pro: data?.pro || null,
				proId: data?.proId || null,
				proRole: data?.proRole || null,
				onboardingRequired: data?.onboardingRequired ?? false,
				pendingInvitationToken: data?.pendingInvitation?.token || null,
				isLoading: authLoading || isLoading || (isAuthenticated && !data && !error),
				error: error ? "Failed to load pro profile" : null,
				refreshPro,
			}}
		>
			{children}
		</ProContext.Provider>
	);
}

export function usePro() {
	const context = useContext(ProContext);
	if (!context) {
		throw new Error("usePro must be used within a ProProvider");
	}
	return context;
}
